/**
 * In-memory token holder — the single place the fetch client reads/writes the access + refresh
 * tokens and the refresh callback. Deliberately framework-free (no React) so the api client can
 * import it without a circular dependency on the auth store.
 *
 * SECURITY (ENGINEERING_STANDARDS): the access token NEVER touches localStorage. It lives only
 * in this module's closure for the tab's lifetime. The refresh token is held here too (the server
 * issues an opaque rotating token in the JSON body; there is no httpOnly-cookie path in the current
 * server contract). On logout / reuse-detection both are cleared.
 */
import type { TokenPair } from "./types";

interface TokenState {
  accessToken: string | null;
  refreshToken: string | null;
  /** epoch ms when the access token expires (now + expires_in*1000), for proactive refresh. */
  expiresAt: number | null;
}

const state: TokenState = {
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
};

/** Called when a refresh family is revoked / session expires — the auth store wires its purge here. */
type SessionEndedHandler = () => void;
let onSessionEnded: SessionEndedHandler | null = null;

export const tokenStore = {
  getAccessToken: (): string | null => state.accessToken,
  getRefreshToken: (): string | null => state.refreshToken,
  getExpiresAt: (): number | null => state.expiresAt,

  /** True ~20s before expiry, so we proactively refresh instead of waiting for a 401. */
  isExpiredOrExpiring(skewMs = 20_000): boolean {
    if (!state.accessToken || state.expiresAt == null) return true;
    return Date.now() >= state.expiresAt - skewMs;
  },

  setFromPair(pair: TokenPair): void {
    state.accessToken = pair.access_token;
    state.refreshToken = pair.refresh_token;
    state.expiresAt = Date.now() + pair.expires_in * 1000;
  },

  clear(): void {
    state.accessToken = null;
    state.refreshToken = null;
    state.expiresAt = null;
  },

  /** Register the auth-store purge (cache clear + route to /login). */
  registerSessionEndedHandler(handler: SessionEndedHandler): void {
    onSessionEnded = handler;
  },

  notifySessionEnded(): void {
    this.clear();
    onSessionEnded?.();
  },
};
